import * as T from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

// City pieces (Quaternius Downtown City MegaKit) and plants (Quaternius Stylized
// Nature MegaKit), both CC0. Each model becomes ONE geometry with a group per
// material, so it can be drawn with instancing: one draw per material.
export type ModelAsset = { geometry: T.BufferGeometry; materials: T.Material[] };

T.Cache.enabled = true; // models share texture files; download each only once

export async function loadModels(names: string[], onProgress?: (fraction: number) => void) {
  const loader = new GLTFLoader();
  const out = new Map<string, ModelAsset>();
  let done = 0;
  await Promise.all(
    names.map(async (name) => {
      // Names are "<folder>/<model>"; ".gltf" is assumed unless a .glb/.gltf extension is given.
      const gltf = await loader.loadAsync(`assets/${name}${/\.(glb|gltf)$/.test(name) ? "" : ".gltf"}`);
      gltf.scene.updateMatrixWorld(true);
      const byMaterial = new Map<T.Material, T.BufferGeometry[]>();
      gltf.scene.traverse((o) => {
        const mesh = o as T.Mesh;
        if (!mesh.isMesh) return;
        const material = mesh.material as T.MeshStandardMaterial;
        // The city kit's vertex colours are wear masks for its own shaders, not colour.
        material.vertexColors = false;
        if (material.map) material.map.anisotropy = 8;
        if (material.transparent && material.name.toLowerCase().includes("leaves")) {
          material.transparent = false;
          material.alphaTest = 0.5;
        }
        const g = (mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry.clone()).applyMatrix4(mesh.matrixWorld);
        for (const key of Object.keys(g.attributes)) if (!["position", "normal", "uv"].includes(key)) g.deleteAttribute(key);
        if (!g.getAttribute("uv")) g.setAttribute("uv", new T.BufferAttribute(new Float32Array(g.getAttribute("position").count * 2), 2));
        if (!g.getAttribute("normal")) g.computeVertexNormals();
        g.clearGroups();
        if (!byMaterial.has(material)) byMaterial.set(material, []);
        byMaterial.get(material)!.push(g);
      });
      const materials: T.Material[] = [];
      const parts: T.BufferGeometry[] = [];
      for (const [material, geos] of byMaterial) {
        const merged = geos.length === 1 ? geos[0] : mergeGeometries(geos);
        if (!merged) continue;
        materials.push(material);
        parts.push(merged);
      }
      const geometry = mergeGeometries(parts, true);
      if (geometry) out.set(name, { geometry, materials });
      onProgress?.(++done / names.length);
    }),
  );
  return out;
}
